import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { FaEdit, FaEnvelope, FaEye, FaPhone, FaTrash, FaWhatsapp } from "react-icons/fa";
import axiosInstance from "../components/api/axiosInstance";
import TablePagination, { DEFAULT_ROWS_PER_PAGE_OPTIONS } from "../components/TablePagination";
import { ResultsMobileCard, ResultsMobileList } from "../components/ResultsMobile";
import PortalDomainSelect from "./PortalDomainSelect";
import PortalAddressFields, {
  EMPTY_ADDRESS_FORM,
  addressPayloadFromForm,
} from "./PortalAddressFields";
import { canDeleteInPortal } from "./portalSession";
import { SupportSortableHeader } from "../pages/SupportSortableHeader";
import PortalAlert from "./PortalAlert";
import PortalConfirmModal from "./PortalConfirmModal";
import PortalFormPage from "./PortalFormPage";
import PortalResultsHead from "./PortalResultsHead";
import { mobileValidationMessage, sanitizeMobileInput } from "./portalPhone";
import { formatPhone } from "../utils/formatters";
import PortalFiltersCollapsible, { usePortalCompactFilters } from "./PortalFiltersCollapsible";
import "../assets/css/DigixLeads.css";
import PortalSelect from "./PortalSelect";

const PORTAL_CLIENTS_ROWS_PER_PAGE_KEY = "portalClientsRowsPerPage";
const PORTAL_CLIENTS_CURRENT_PAGE_KEY = "portalClientsCurrentPage";

const CLIENT_STATUSES = ["Active", "Inactive"];
const BUSINESS_SUB_TYPES = ["Project", "Service", "Both"];

const EMPTY_FORM = {
  clientName: "",
  contactPerson: "",
  mobileNumber: "",
  altNumber: "",
  email: "",
  ...EMPTY_ADDRESS_FORM,
  website: "",
  domain: "",
  businessSubType: "",
  description: "",
  status: "Active",
};

function display(value) {
  if (value == null || String(value).trim() === "") return "—";
  return String(value);
}

function phoneDisplay(c) {
  if (!c.Mobile_Number) return "—";
  const digits = `${c.Country_Code || ""}${c.Mobile_Number}`.replace(/\D/g, "");
  return formatPhone(digits) || "—";
}

function emailLink(email) {
  const value = String(email || "").trim();
  if (!value) return null;
  return (
    <a href={`mailto:${value}`} onClick={(e) => e.stopPropagation()}>
      {value}
    </a>
  );
}

function phoneLink(c) {
  const label = phoneDisplay(c);
  if (label === "—") return null;
  const tel = `${c.Country_Code || ""}${c.Mobile_Number}`.replace(/[^\d+]/g, "");
  return (
    <a href={`tel:${tel}`} onClick={(e) => e.stopPropagation()}>
      {label}
    </a>
  );
}

function statusBadgeClass(status) {
  const s = String(status || "").toLowerCase();
  if (s === "active") return "cportal-badge cportal-badge--success";
  if (s === "inactive") return "cportal-badge cportal-badge--danger";
  return "cportal-badge";
}

function servicesSummary(services) {
  if (!Array.isArray(services) || services.length === 0) return "—";
  const names = services
    .filter((s) => s.Status === "Active")
    .map((s) => s.Service_Name)
    .filter(Boolean);
  return names.length ? names.join(", ") : "—";
}

function projectsSummary(projects) {
  if (!Array.isArray(projects) || projects.length === 0) return "—";
  const names = projects
    .filter((p) => p.Status === "Active")
    .map((p) => {
      if (p.Short_Code && p.Project_Name) return `${p.Short_Code} (${p.Project_Name})`;
      return p.Short_Code || p.Project_Name;
    })
    .filter(Boolean);
  return names.length ? names.join(", ") : "—";
}

const SORT_ACCESSORS = {
  clientName: (c) => (c.Client_Name || "").toLowerCase(),
  contactPerson: (c) => (c.Contact_Person || "").toLowerCase(),
  mobileNumber: (c) => (c.Mobile_Number || "").toLowerCase(),
  email: (c) => (c.Email || "").toLowerCase(),
  domain: (c) => (c.Domain || "").toLowerCase(),
  status: (c) => (c.Status || "").toLowerCase(),
  projects: (c) => projectsSummary(c.Projects).toLowerCase(),
  services: (c) => servicesSummary(c.Services).toLowerCase(),
};

const COLUMNS = [
  { key: "clientName", label: "Client name" },
  { key: "contactPerson", label: "Contact person" },
  { key: "mobileNumber", label: "Mobile" },
  { key: "email", label: "Email" },
  { key: "domain", label: "Business Type" },
  { key: "projects", label: "Projects" },
  { key: "services", label: "Services" },
  { key: "status", label: "Status" },
];

const DATE_PRESETS = [
  { id: "all", label: "All time" },
  { id: "today", label: "Today" },
  { id: "7", label: "Last 7 days" },
  { id: "30", label: "Last 30 days" },
  { id: "month", label: "This month" },
  { id: "custom", label: "Custom" },
];

function startOfDay(d) {
  const x = new Date(d);
  x.setHours(0, 0, 0, 0);
  return x;
}

function endOfDay(d) {
  const x = new Date(d);
  x.setHours(23, 59, 59, 999);
  return x;
}

function presetRange(preset, fromDate, toDate) {
  const now = new Date();
  switch (preset) {
    case "today":
      return { from: startOfDay(now), to: endOfDay(now) };
    case "7": {
      const from = startOfDay(now);
      from.setDate(from.getDate() - 6);
      return { from, to: endOfDay(now) };
    }
    case "30": {
      const from = startOfDay(now);
      from.setDate(from.getDate() - 29);
      return { from, to: endOfDay(now) };
    }
    case "month":
      return { from: startOfDay(new Date(now.getFullYear(), now.getMonth(), 1)), to: endOfDay(now) };
    case "custom":
      return {
        from: fromDate ? startOfDay(new Date(fromDate)) : null,
        to: toDate ? endOfDay(new Date(toDate)) : null,
      };
    default:
      return { from: null, to: null };
  }
}

function TenantClients({ createMode = false }) {
  const navigate = useNavigate();
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [sortKey, setSortKey] = useState("clientName");
  const [sortDir, setSortDir] = useState("asc");
  const [currentPage, setCurrentPage] = useState(() => {
    try {
      const saved = localStorage.getItem(PORTAL_CLIENTS_CURRENT_PAGE_KEY);
      if (!saved) return 1;
      const parsed = parseInt(saved, 10);
      return parsed > 0 ? parsed : 1;
    } catch {
      return 1;
    }
  });
  const [rowsPerPage, setRowsPerPage] = useState(() => {
    try {
      const saved = localStorage.getItem(PORTAL_CLIENTS_ROWS_PER_PAGE_KEY);
      const parsed = parseInt(saved, 10);
      if (DEFAULT_ROWS_PER_PAGE_OPTIONS.includes(parsed)) return parsed;
    } catch {
      /* ignore */
    }
    return 10;
  });
  const [form, setForm] = useState(EMPTY_FORM);
  const [submitting, setSubmitting] = useState(false);
  const [formError, setFormError] = useState("");
  const [search, setSearch] = useState("");
  const [resultKeyword, setResultKeyword] = useState("");
  const [datePreset, setDatePreset] = useState("all");
  const [fromDate, setFromDate] = useState("");
  const [toDate, setToDate] = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const [deactivatingId, setDeactivatingId] = useState(null);
  const [deactivateClient, setDeactivateClient] = useState(null);
  const canDelete = useMemo(() => canDeleteInPortal(), []);
  const { filtersOpen, setFiltersOpen, isCompactViewport } = usePortalCompactFilters();

  const toggleSort = (key) => {
    setCurrentPage(1);
    if (key === sortKey) {
      setSortDir((d) => (d === "asc" ? "desc" : "asc"));
    } else {
      setSortKey(key);
      setSortDir("asc");
    }
  };

  const handleRowsPerPageChange = (next) => {
    setRowsPerPage(next);
    setCurrentPage(1);
    localStorage.setItem(PORTAL_CLIENTS_ROWS_PER_PAGE_KEY, String(next));
  };

  const loadClients = useCallback(async () => {
    const res = await axiosInstance.get("/client/tenant-clients");
    setClients(Array.isArray(res.data?.data) ? res.data.data : []);
  }, []);

  useEffect(() => {
    if (createMode) return undefined;
    let active = true;
    (async () => {
      try {
        await loadClients();
      } catch (_) {
        if (active) setError("Could not load clients.");
      } finally {
        if (active) setLoading(false);
      }
    })();
    return () => {
      active = false;
    };
  }, [loadClients, createMode]);

  const filtered = useMemo(() => {
    let list = clients;

    const { from, to } = presetRange(datePreset, fromDate, toDate);
    if (from || to) {
      list = list.filter((c) => {
        if (!c.Created_On) return false;
        const created = new Date(c.Created_On);
        if (Number.isNaN(created.getTime())) return false;
        if (from && created < from) return false;
        if (to && created > to) return false;
        return true;
      });
    }

    if (statusFilter) list = list.filter((c) => c.Status === statusFilter);

    const q = search.trim().toLowerCase();
    if (q) {
      list = list.filter((c) =>
        [
          c.Client_Name,
          c.Contact_Person,
          c.Mobile_Number,
          c.Alt_Number,
          c.Email,
          c.Address,
          c.Website,
          c.Domain,
          c.Description,
        ]
          .filter(Boolean)
          .some((v) => String(v).toLowerCase().includes(q))
      );
    }

    return list;
  }, [clients, search, datePreset, fromDate, toDate, statusFilter]);

  const resultFiltered = useMemo(() => {
    const q = resultKeyword.trim().toLowerCase();
    if (!q) return filtered;
    return filtered.filter((c) =>
      [
        c.Client_Name,
        c.Contact_Person,
        c.Mobile_Number,
        c.Country_Code,
        c.Alt_Number,
        c.Email,
        c.Status,
        c.City,
        c.Address,
        c.Website,
        c.Domain,
        c.Description,
        projectsSummary(c.Projects),
        servicesSummary(c.Services),
      ]
        .filter(Boolean)
        .some((v) => String(v).toLowerCase().includes(q))
    );
  }, [filtered, resultKeyword]);

  const hasActiveFilters = datePreset !== "all" || !!statusFilter || !!search;

  const clearFilters = () => {
    setDatePreset("all");
    setFromDate("");
    setToDate("");
    setStatusFilter("");
    setSearch("");
    setCurrentPage(1);
  };

  const sorted = useMemo(() => {
    const accessor = SORT_ACCESSORS[sortKey];
    if (!accessor) return resultFiltered;
    const dir = sortDir === "asc" ? 1 : -1;
    return [...resultFiltered].sort((a, b) => {
      const av = accessor(a);
      const bv = accessor(b);
      if (av < bv) return -1 * dir;
      if (av > bv) return 1 * dir;
      return 0;
    });
  }, [resultFiltered, sortKey, sortDir]);

  const totalPages = Math.max(1, Math.ceil(sorted.length / rowsPerPage));
  const startIndex = (currentPage - 1) * rowsPerPage;
  const pageRows = sorted.slice(startIndex, startIndex + rowsPerPage);

  useEffect(() => {
    if (currentPage > 0) {
      localStorage.setItem(PORTAL_CLIENTS_CURRENT_PAGE_KEY, String(currentPage));
    }
  }, [currentPage]);

  const skipFilterPageResetRef = React.useRef(true);
  useEffect(() => {
    if (skipFilterPageResetRef.current) {
      skipFilterPageResetRef.current = false;
      return;
    }
    setCurrentPage(1);
  }, [search, resultKeyword, datePreset, fromDate, toDate, statusFilter]);

  useEffect(() => {
    if (totalPages > 0 && currentPage > totalPages) setCurrentPage(1);
  }, [totalPages, currentPage]);

  const setField = (name, value) => setForm((f) => ({ ...f, [name]: value }));

  useEffect(() => {
    if (!createMode) return;
    setForm(EMPTY_FORM);
    setFormError("");
  }, [createMode]);

  const closeAddForm = () => navigate("/portal/clients");

  const buildPayload = () => ({
    Client_Name: form.clientName.trim(),
    Contact_Person: form.contactPerson.trim() || null,
    Mobile_Number: form.mobileNumber.trim() || null,
    Alt_Number: form.altNumber.trim() || null,
    Email: form.email.trim() || null,
    ...addressPayloadFromForm(form),
    Website: form.website.trim() || null,
    Domain: form.domain.trim(),
    Business_Sub_Type: form.businessSubType.trim(),
    Description: form.description.trim() || null,
    Status: form.status || "Active",
  });

  const requestDeactivate = (client) => {
    if (client.Status === "Inactive") return;
    setDeactivateClient(client);
  };

  const closeDeactivateModal = () => {
    if (deactivatingId) return;
    setDeactivateClient(null);
  };

  const confirmDeactivate = async () => {
    const client = deactivateClient;
    if (!client?.Tenant_Client_Id) return;

    setDeactivatingId(client.Tenant_Client_Id);
    setError("");
    try {
      await axiosInstance.delete(`/client/tenant-clients/${client.Tenant_Client_Id}`);
      await loadClients();
      setDeactivateClient(null);
    } catch (err) {
      setError(err.response?.data?.message || "Could not deactivate client.");
    } finally {
      setDeactivatingId(null);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.clientName.trim()) {
      setFormError("Client name is required.");
      return;
    }
    if (!form.contactPerson.trim()) {
      setFormError("Contact person is required.");
      return;
    }
    if (!form.domain.trim()) {
      setFormError("Business Type is required.");
      return;
    }
    if (!BUSINESS_SUB_TYPES.includes(form.businessSubType)) {
      setFormError("Business Sub Type is required.");
      return;
    }

    const mobileError = mobileValidationMessage(form.mobileNumber, {
      required: true,
      label: "Mobile number",
    });
    if (mobileError) {
      setFormError(mobileError);
      return;
    }
    const altNumberError = mobileValidationMessage(form.altNumber, { label: "Alternate number" });
    if (altNumberError) {
      setFormError(altNumberError);
      return;
    }

    setSubmitting(true);
    setFormError("");
    try {
      await axiosInstance.post("/client/tenant-clients", buildPayload());
      closeAddForm();
    } catch (err) {
      setFormError(
        err.response?.data?.message ||
          err.response?.data?.error ||
          "Could not create client."
      );
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="cportal-page cportal-page--clients">
      {!createMode && (
        <>
      <div className="cportal-page-head">
        <h1>Clients</h1>
      </div>

      <PortalAlert type="error" message={error} onClear={() => setError("")} />

      <PortalFiltersCollapsible
        search={search}
        onSearchChange={setSearch}
        searchPlaceholder="Search client, contact, phone, email, business"
        searchAriaLabel="Search clients"
        filtersOpen={filtersOpen}
        setFiltersOpen={setFiltersOpen}
        isCompactViewport={isCompactViewport}
        hasActiveFilters={hasActiveFilters}
        onClear={clearFilters}
        actions={
          <button
            type="button"
            className="cportal-btn cportal-btn-primary"
            onClick={() => navigate("/portal/clients/new")}
          >
            Add Client
          </button>
        }
      >
        <div className="cportal-filter-presets">
          {DATE_PRESETS.map((p) => (
            <button
              key={p.id}
              type="button"
              className={`cportal-chip ${datePreset === p.id ? "active" : ""}`}
              onClick={() => setDatePreset(p.id)}
            >
              {p.label}
            </button>
          ))}
        </div>

        {datePreset === "custom" && (
          <div className="cportal-filter-dates">
            <label className="cportal-field">
              <span>From date</span>
              <input type="date" value={fromDate} onChange={(e) => setFromDate(e.target.value)} />
            </label>
            <label className="cportal-field">
              <span>To date</span>
              <input type="date" value={toDate} onChange={(e) => setToDate(e.target.value)} />
            </label>
          </div>
        )}

        <div className="cportal-filter-row">
          <select value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)}>
            <option value="">All status</option>
            {CLIENT_STATUSES.map((s) => (
              <option key={s} value={s}>
                {s}
              </option>
            ))}
          </select>
        </div>
      </PortalFiltersCollapsible>

      <div className="cportal-card">
        <PortalResultsHead
          heading="Clients"
          count={sorted.length}
          keyword={resultKeyword}
          onKeywordChange={setResultKeyword}
        />

        {loading ? (
          <p className="cportal-muted">Loading…</p>
        ) : sorted.length === 0 ? (
          <p className="cportal-muted">
            {clients.length === 0 ? "No clients added yet." : "No clients match your filters."}
          </p>
        ) : (
          <>
          <div className="cportal-table-wrap co-desktop-table">
            <table className="cportal-table">
              <thead>
                <tr>
                  {COLUMNS.map((col) => (
                    <th key={col.key} className="cportal-th-sortable">
                      <SupportSortableHeader
                        columnId={col.key}
                        label={col.label}
                        sortColumn={sortKey}
                        sortDirection={sortDir}
                        onSort={toggleSort}
                      />
                    </th>
                  ))}
                  <th className="cportal-th-actions">Actions</th>
                </tr>
              </thead>
              <tbody>
                {pageRows.map((client) => (
                  <tr key={client.Tenant_Client_Id}>
                    <td>
                      <button
                        type="button"
                        className="cportal-table-link"
                        onClick={() =>
                          navigate(`/portal/clients/${client.Tenant_Client_Id}`)
                        }
                      >
                        {display(client.Client_Name)}
                      </button>
                    </td>
                    <td>{display(client.Contact_Person)}</td>
                    <td>{phoneDisplay(client)}</td>
                    <td>{display(client.Email)}</td>
                    <td>{display(client.Domain)}</td>
                    <td>{projectsSummary(client.Projects)}</td>
                    <td>{servicesSummary(client.Services)}</td>
                    <td>
                      <span className={statusBadgeClass(client.Status)}>{display(client.Status)}</span>
                    </td>
                    <td className="cportal-td-actions">
                      <div className="cportal-re-actions">
                        {(() => {
                          const phoneDigits = `${client.Country_Code || ""}${client.Mobile_Number || ""}`.replace(
                            /[^\d+]/g,
                            ""
                          );
                          const telHref = client.Mobile_Number ? `tel:${phoneDigits}` : null;
                          const waDigits = phoneDigits.replace(/\D/g, "");
                          const waHref =
                            client.Mobile_Number && waDigits.length >= 10 ? `https://wa.me/${waDigits}` : null;
                          const mailHref = client.Email ? `mailto:${client.Email}` : null;
                          return (
                            <>
                              {waHref ? (
                                <a
                                  className="re-lead-action re-lead-action--whatsapp"
                                  href={waHref}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  title={`WhatsApp ${phoneDisplay(client)}`}
                                  aria-label="Open WhatsApp"
                                >
                                  <FaWhatsapp size={15} />
                                </a>
                              ) : (
                                <span
                                  className="re-lead-action re-lead-action--whatsapp re-lead-action--disabled"
                                  title="WhatsApp unavailable"
                                  aria-disabled="true"
                                >
                                  <FaWhatsapp size={15} />
                                </span>
                              )}
                              {telHref ? (
                                <a
                                  className="re-lead-action re-lead-action--call"
                                  href={telHref}
                                  title={`Call ${phoneDisplay(client)}`}
                                  aria-label="Call client"
                                >
                                  <FaPhone size={12} style={{ transform: "scaleX(-1)" }} />
                                </a>
                              ) : (
                                <span
                                  className="re-lead-action re-lead-action--call re-lead-action--disabled"
                                  title="Call unavailable"
                                  aria-disabled="true"
                                >
                                  <FaPhone size={12} style={{ transform: "scaleX(-1)" }} />
                                </span>
                              )}
                              {mailHref ? (
                                <a
                                  className="re-lead-action re-lead-action--email"
                                  href={mailHref}
                                  title={`Email ${client.Email}`}
                                  aria-label="Email client"
                                >
                                  <FaEnvelope size={13} />
                                </a>
                              ) : (
                                <span
                                  className="re-lead-action re-lead-action--email re-lead-action--disabled"
                                  title="Email unavailable"
                                  aria-disabled="true"
                                >
                                  <FaEnvelope size={13} />
                                </span>
                              )}
                            </>
                          );
                        })()}
                        <button
                          type="button"
                          className="re-lead-action re-lead-action--view"
                          onClick={() => navigate(`/portal/clients/${client.Tenant_Client_Id}`)}
                          title="View client"
                          aria-label="View client"
                        >
                          <FaEye size={16} />
                        </button>
                        <button
                          type="button"
                          className="re-lead-action re-lead-action--edit"
                          onClick={() =>
                            navigate(`/portal/clients/${client.Tenant_Client_Id}`, {
                              state: { edit: true },
                            })
                          }
                          title="Edit client"
                          aria-label="Edit client"
                        >
                          <FaEdit size={16} />
                        </button>
                        {canDelete && (
                          <button
                            type="button"
                            className="re-lead-action re-lead-action--delete"
                            onClick={() => requestDeactivate(client)}
                            disabled={
                              client.Status === "Inactive" ||
                              deactivatingId === client.Tenant_Client_Id
                            }
                            title={
                              client.Status === "Inactive"
                                ? "Already inactive"
                                : "Deactivate client"
                            }
                            aria-label="Deactivate client"
                          >
                            {deactivatingId === client.Tenant_Client_Id ? "…" : <FaTrash size={16} />}
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <ResultsMobileList
            emptyLabel={
              clients.length === 0 ? "No clients added yet." : "No clients match your filters."
            }
            className="cportal-clients-mobile"
          >
            {pageRows.map((client) => {
              const id = client.Tenant_Client_Id;
              const status = client.Status || "—";
              const statusTone =
                String(status).toLowerCase() === "active"
                  ? "success"
                  : String(status).toLowerCase() === "inactive"
                    ? "danger"
                    : "neutral";
              const phoneDigits = `${client.Country_Code || ""}${client.Mobile_Number || ""}`.replace(
                /[^\d+]/g,
                ""
              );
              const telHref = client.Mobile_Number ? `tel:${phoneDigits}` : null;
              const waDigits = phoneDigits.replace(/\D/g, "");
              const waHref =
                client.Mobile_Number && waDigits.length >= 10 ? `https://wa.me/${waDigits}` : null;
              return (
                <ResultsMobileCard
                  key={id}
                  title={display(client.Client_Name)}
                  meta={[
                    { label: status, tone: statusTone, status: true },
                    client.Domain ? { label: client.Domain, tone: "info" } : null,
                  ].filter(Boolean)}
                  fieldsLayout="stacked"
                  fields={[
                    { label: "Contact", value: client.Contact_Person },
                    { label: "Mobile", value: phoneLink(client) },
                    { label: "Email", value: emailLink(client.Email) },
                    {
                      label: "Projects",
                      value:
                        projectsSummary(client.Projects) !== "—"
                          ? projectsSummary(client.Projects)
                          : null,
                    },
                    {
                      label: "Services",
                      value:
                        servicesSummary(client.Services) !== "—"
                          ? servicesSummary(client.Services)
                          : null,
                    },
                  ]}
                  onOpen={() => navigate(`/portal/clients/${id}`)}
                  actions={
                    <div className="co-mcard__action-grid">
                      {telHref ? (
                        <a className="co-maction" href={telHref} title="Call" aria-label="Call">
                          <FaPhone size={14} style={{ transform: "scaleX(-1)" }} />
                          <span>Call</span>
                        </a>
                      ) : null}
                      {waHref ? (
                        <a
                          className="co-maction co-maction--whatsapp"
                          href={waHref}
                          target="_blank"
                          rel="noopener noreferrer"
                          title="WhatsApp"
                          aria-label="WhatsApp"
                        >
                          <FaWhatsapp size={14} />
                          <span>WA</span>
                        </a>
                      ) : null}
                      {client.Email ? (
                        <a
                          className="co-maction"
                          href={`mailto:${client.Email}`}
                          title="Email"
                          aria-label="Email"
                        >
                          <FaEnvelope size={14} />
                          <span>Email</span>
                        </a>
                      ) : null}
                      <button
                        type="button"
                        className="co-maction"
                        title="View"
                        aria-label="View"
                        onClick={() => navigate(`/portal/clients/${id}`)}
                      >
                        <FaEye size={14} />
                        <span>View</span>
                      </button>
                      <button
                        type="button"
                        className="co-maction"
                        title="Edit"
                        aria-label="Edit"
                        onClick={() =>
                          navigate(`/portal/clients/${id}`, { state: { edit: true } })
                        }
                      >
                        <FaEdit size={14} />
                        <span>Edit</span>
                      </button>
                      {canDelete ? (
                        <button
                          type="button"
                          className="co-maction co-maction--danger"
                          title="Deactivate"
                          aria-label="Deactivate"
                          disabled={
                            client.Status === "Inactive" || deactivatingId === id
                          }
                          onClick={() => requestDeactivate(client)}
                        >
                          <FaTrash size={14} />
                          <span>Remove</span>
                        </button>
                      ) : null}
                    </div>
                  }
                />
              );
            })}
          </ResultsMobileList>
          </>
        )}

        {sorted.length > 0 && !loading && (
          <TablePagination
            currentPage={currentPage}
            totalItems={sorted.length}
            rowsPerPage={rowsPerPage}
            rowsPerPageOptions={DEFAULT_ROWS_PER_PAGE_OPTIONS}
            onPageChange={setCurrentPage}
            onRowsPerPageChange={handleRowsPerPageChange}
            itemLabel="clients"
            rowsPerPageSelectId="portal-clients-rows-per-page"
          />
        )}
      </div>

        </>
      )}

      {createMode && (
        <PortalFormPage
          title="Add Client"
          backLabel="Back to clients"
          onBack={closeAddForm}
          onSubmit={handleSubmit}
          submitting={submitting}
          submitLabel="Create Client"
          submittingLabel="Creating…"
          className="cportal-form-page--two-col"
        >
              <PortalAlert type="error" message={formError} onClear={() => setFormError("")} />
              <div className="cportal-form-grid cportal-form-grid--3">
                <div className="cportal-field">
                  <label>
                    Client name <span className="cportal-required">*</span>
                  </label>
                  <input
                    value={form.clientName}
                    onChange={(e) => setField("clientName", e.target.value)}
                    required
                  />
                </div>
                <div className="cportal-field">
                  <label>
                    Contact person <span className="cportal-required">*</span>
                  </label>
                  <input
                    value={form.contactPerson}
                    onChange={(e) => setField("contactPerson", e.target.value)}
                    required
                  />
                </div>
                <div className="cportal-field">
                  <label>
                    Mobile number <span className="cportal-required">*</span>
                  </label>
                  <input
                    value={form.mobileNumber}
                    onChange={(e) => setField("mobileNumber", sanitizeMobileInput(e.target.value))}
                    inputMode="numeric"
                    maxLength={10}
                    placeholder="10-digit number"
                    required
                  />
                </div>
                <div className="cportal-field">
                  <label>Alt number</label>
                  <input
                    value={form.altNumber}
                    onChange={(e) => setField("altNumber", sanitizeMobileInput(e.target.value))}
                    inputMode="numeric"
                    maxLength={10}
                    placeholder="10-digit number"
                  />
                </div>
                <div className="cportal-field">
                  <label>Email</label>
                  <input
                    type="email"
                    value={form.email}
                    onChange={(e) => setField("email", e.target.value)}
                  />
                </div>
                <div className="cportal-field">
                  <label>Website</label>
                  <input
                    value={form.website}
                    onChange={(e) => setField("website", e.target.value)}
                    placeholder="https://"
                  />
                </div>
                <PortalDomainSelect
                  value={form.domain}
                  onChange={(value) => setField("domain", value)}
                  required
                />
                <div className="cportal-field">
                  <label>
                    Business Sub Type <span className="cportal-required">*</span>
                  </label>
                  <PortalSelect
                    value={form.businessSubType}
                    onChange={(e) => setField("businessSubType", e.target.value)}
                  >
                    <option value="">Select sub type</option>
                    {BUSINESS_SUB_TYPES.map((s) => (
                      <option key={s} value={s}>
                        {s}
                      </option>
                    ))}
                  </PortalSelect>
                </div>
                <div className="cportal-field">
                  <label>
                    Status <span className="cportal-required">*</span>
                  </label>
                  <PortalSelect value={form.status} onChange={(e) => setField("status", e.target.value)}>
                    {CLIENT_STATUSES.map((s) => (
                      <option key={s} value={s}>
                        {s}
                      </option>
                    ))}
                  </PortalSelect>
                </div>
              </div>

              <div className="cportal-form-grid cportal-form-grid--3 cportal-form-grid--spaced-top">
                <PortalAddressFields form={form} setForm={setForm} />
                <div className="cportal-field cportal-field--full">
                  <label>Description</label>
                  <textarea
                    rows={3}
                    value={form.description}
                    onChange={(e) => setField("description", e.target.value)}
                  />
                </div>
              </div>
        </PortalFormPage>
      )}

      <PortalConfirmModal
        open={!!deactivateClient}
        title="Deactivate Client"
        message={
          deactivateClient ? (
            <p>
              Deactivate <strong>{deactivateClient.Client_Name}</strong>? Their status will be set to
              Inactive.
            </p>
          ) : null
        }
        confirmLabel="Yes"
        cancelLabel="No"
        confirming={!!deactivatingId}
        confirmingLabel="Deactivating…"
        onConfirm={confirmDeactivate}
        onClose={closeDeactivateModal}
      />
    </div>
  );
}

export default TenantClients;
